/**
 * Récupération d'altitude pour une trace.
 *
 * Un GPX d'organisateur ne porte souvent aucune altitude : ni profil, ni
 * dénivelé, ni marqueurs sur la courbe. La récupération les débloque sur
 * n'importe quel fichier.
 *
 * Le fichier source n'est **jamais modifié** : les altitudes obtenues vivent
 * dans le cache local, réappliquées au chargement. C'est ce qui permet de
 * revenir au tracé d'origine après un routage sans redemander quoi que ce soit
 * au réseau.
 *
 * Référence : `ElevationController.kt`, `docs/altitude.md`.
 */

import { resegmentLike, type GpxTrack } from '../core/track'
import type { Storage } from '../core/storage'
import type { Translator } from '../i18n'
import type { TrackPoint } from '../core/types'
import {
  cumulativeDistances,
  fetchElevations,
  sampleIndices,
} from '../core/elevationFetch'
import { runElevationTaskAsync } from '../core/elevationWorker'
import type { Shell } from './shell'

/** Clé du cache d'altitudes d'une trace. */
export function elevationCacheKey(trackId: string): string {
  return `ele_${trackId}`
}

/**
 * Altitudes enregistrées pour une trace.
 *
 * Le nombre de points est conservé avec elles : un fichier source remplacé
 * entre deux sessions donnerait un tableau désaligné, qui décalerait tout le
 * profil sans rien signaler.
 */
interface CachedElevations {
  readonly count: number
  readonly elevations: number[]
}

export interface ElevationDeps {
  readonly shell: Shell
  readonly translator: Translator
  readonly storage: Storage
  readonly tracks: () => readonly GpxTrack[]
  /** Redessine la trace et enregistre les modifications. */
  readonly onTrackChanged: (track: GpxTrack) => void
  /** Rafraîchit la ligne du tiroir, pour l'avancement. */
  readonly onProgress: () => void
}

export class ElevationController {
  /** Récupérations en cours, une par trace au plus. */
  #jobs = new Map<string, AbortController>()

  constructor(private readonly deps: ElevationDeps) {}

  /**
   * La trace gagnerait-elle à une récupération ?
   *
   * Une trace sans point n'a rien à interroger ; une trace dont le profil est
   * déjà exploitable n'a rien à y gagner.
   */
  needsElevation(track: GpxTrack): boolean {
    return track.allPoints.length > 0 && !track.hasElevationData
  }

  isRunning(track: GpxTrack): boolean {
    return this.#jobs.has(track.id)
  }

  /**
   * Récupère les altitudes et les applique.
   *
   * Le cache local est consulté avant le réseau : revenir au tracé d'origine
   * après un routage réapplique alors le profil sans un seul appel.
   */
  async fetch(track: GpxTrack, options: { quiet?: boolean } = {}): Promise<void> {
    if (this.#jobs.has(track.id)) return

    const t = this.deps.translator
    const points = track.allPoints
    if (points.length === 0) return

    const cached = await this.#readCache(track)
    if (cached) {
      this.#assign(track, cached)
      if (options.quiet !== true) {
        this.deps.shell.showSnackbar(
          t.t('elevation_applied', { gain: Math.round(track.elevationGain) }),
        )
      }
      return
    }

    const controller = new AbortController()
    this.#jobs.set(track.id, controller)

    track.taskProgress = 0
    track.taskLabel = t.t('elevation_progress')
    this.deps.onProgress()

    try {
      const cumulative = cumulativeDistances(points)
      const indices = sampleIndices(cumulative)
      const samples = indices.map((index) => points[index]!)

      const elevations = await fetchElevations(samples, {
        signal: controller.signal,
        onProgress: ({ ratio }) => {
          track.taskProgress = ratio
          this.deps.onProgress()
        },
      })

      if (elevations === null) {
        if (options.quiet !== true) {
          this.deps.shell.showSnackbar(t.t('elevation_error'))
        }
        return
      }

      // La trace a pu être supprimée ou retracée pendant l'appel.
      if (!this.deps.tracks().includes(track)) return
      if (track.allPoints.length !== points.length) return

      const updated = await runElevationTaskAsync({
        kind: 'sample-elevations',
        points,
        sampleIdx: indices,
        sampleEle: elevations,
      })

      await this.#writeCache(track, updated)
      this.#assign(track, updated)

      if (options.quiet !== true) {
        this.deps.shell.showSnackbar(
          t.t('elevation_applied', { gain: Math.round(track.elevationGain) }),
        )
      }
    } catch (error) {
      // Une annulation est attendue — trace supprimée, autre récupération —
      // et ne mérite rien. Toute autre erreur est un défaut, et un défaut
      // avalé en silence est indiscernable d'une application figée.
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('[VeloGPX] Récupération d’altitude interrompue', error)
        if (options.quiet !== true) {
          this.deps.shell.showSnackbar(t.t('elevation_error'))
        }
      }
    } finally {
      // Nettoyage **seulement si l'on est encore le job courant**. Une
      // annulation retire l'entrée aussitôt, l'utilisateur peut relancer, et
      // cette tâche-ci peut mettre encore plusieurs secondes à se rendre
      // compte de son abandon : son `finally` tardif effaçait alors l'entrée
      // du job NEUF. La garde anti-double-tap tombait, et l'annulation ne
      // trouvait plus rien à interrompre.
      //
      // Tout le nettoyage est sous la garde, pas seulement l'entrée : sinon la
      // tâche abandonnée éteindrait l'avancement du job neuf, qui tourne
      // pourtant.
      if (this.#jobs.get(track.id) === controller) {
        this.#jobs.delete(track.id)
        track.taskProgress = null
        track.taskLabel = null
        this.deps.onProgress()
      }
    }
  }

  /**
   * Abandonne la récupération d'une trace supprimée.
   *
   * L'entrée n'est **pas** retirée ici : la tâche la rend dans son `finally`,
   * une fois son abandon constaté. Voir `RouteController.cancel`.
   */
  cancel(track: GpxTrack): void {
    this.#jobs.get(track.id)?.abort()
    void this.deps.storage.deleteCache(elevationCacheKey(track.id))
  }

  /**
   * Réapplique les altitudes enregistrées, sans réseau.
   *
   * Appelée à la restauration : le fichier source n'ayant jamais été modifié,
   * c'est le cache qui porte le profil récupéré.
   */
  async restore(track: GpxTrack): Promise<void> {
    const cached = await this.#readCache(track)
    if (cached) this.#assign(track, cached)
  }

  async #readCache(track: GpxTrack): Promise<TrackPoint[] | null> {
    const points = track.allPoints
    if (points.length === 0) return null

    const cached = await this.deps.storage.getCache<CachedElevations>(
      elevationCacheKey(track.id),
    )

    if (!cached || cached.count !== points.length) {
      // Décompte différent : le fichier source a changé, les altitudes ne
      // correspondent plus à rien. On les oublie plutôt que de les décaler.
      if (cached) {
        await this.deps.storage.deleteCache(elevationCacheKey(track.id))
      }
      return null
    }

    return points.map((point, i) => ({ ...point, ele: cached.elevations[i]! }))
  }

  async #writeCache(track: GpxTrack, points: readonly TrackPoint[]): Promise<void> {
    await this.deps.storage.putCache<CachedElevations>(
      elevationCacheKey(track.id),
      {
        count: points.length,
        elevations: points.map((point) => point.ele ?? 0),
      },
    )
  }

  /**
   * Remplace la géométrie par une copie portant les altitudes.
   *
   * Les segments sont **réassignés** et jamais modifiés sur place : les caches
   * dérivés de la trace se fient à son numéro de version, qui ne change que
   * par ce passage.
   */
  #assign(track: GpxTrack, points: readonly TrackPoint[]): void {
    // La découpe en `<trkseg>` est conservée : elle fait partie de ce que le
    // fichier dit de la trace — coupure d'enregistrement, tunnel —, et Android
    // reconstruit chaque segment à sa place. Aplatir ici suffisait à faire
    // exporter aux deux applications des fichiers de structure différente dès
    // qu'on acceptait la proposition d'altitude à l'import.
    track.setSegments(resegmentLike(track.segments, points))
    track.geometryModified = true
    this.deps.onTrackChanged(track)
  }
}
